/**
 * WorkScheduleMatrix — rrjeta e dendur e orarit mujor
 *
 * Shfaq muajin si matricë dense me slot 30-minutësh.
 * Çdo rresht = 1 ditë. Çdo kolonë e djathtë = interval 30 min.
 * Grupimi: javë → rreshtat ditore → rreshti përmbledhës i javës.
 *
 * Props:
 *   rows          — array i ditove nga /api/reports/monthly-work-schedule
 *   scheduleStart — "07:00" (string, opsional)
 *   scheduleEnd   — "16:00" (string, opsional)
 *   isAdmin       — boolean, shfaq butonin Edit
 *   readOnly      — boolean, kontrata e qartë "vetëm shikim": kur është true,
 *                   kolona e korrigjimit nuk vizatohet fare dhe asnjë qelizë
 *                   nuk klikohet — as për admin. Kjo është ndryshe nga
 *                   `isAdmin=false`, që ende hap një pamje ditore.
 *   onEditDay     — fn(row) kur klikohet Edit
 */
import React, { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditNoteIcon            from '@mui/icons-material/EditNote';
import { useTranslation } from 'react-i18next';

// ── Constants ─────────────────────────────────────────────────────────────────

const INTERVAL_MIN  = 10;
const DEFAULT_START = '06:00';
const DEFAULT_END   = '20:00';

// Ngjyrat e slot-eve — puna e mbushur me të zezë, si te rrjeta klasike e orëve.
const SLOT_BG = {
  work:        '#000000',
  overtime:    '#6a1b9a',
  break:       '#e65100',
  absent:      'rgba(211,47,47,0.45)',
  // Planned-but-not-worked: the assigned orar window on an absent work day —
  // a strong, saturated blue (matches the "Planifikuar" legend), clearly
  // visible yet distinct from solid-black worked time.
  planned:     'rgba(21,101,192,0.55)',
  dayoff:      'transparent',
  outside:     '#e0e0e0',
  in_progress: '#1a1a1a',
  // Slot i hequr manualisht nga admin
  excluded:    'rgba(211,47,47,0.32)',
};

// Ngjyrat e rreshtit (background i të gjithë rreshtit)
const ROW_BG = {
  absent: 'rgba(211,47,47,0.16)',
  dayoff: 'rgba(0,0,0,0.08)',
  normal: 'transparent',
};

// Stilet bazë të tabelës (shmang overhead-in e MUI sx për qelizat e dendura)
const BASE_TH = {
  padding: '2px 4px',
  border: '1px solid #d0d0d0',
  fontSize: 10,
  fontWeight: 700,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  background: '#f0f0f0',
  color: '#333',
  height: 22,
  lineHeight: '18px',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const BASE_TD = {
  padding: '1px 4px',
  border: '1px solid #e8e8e8',
  fontSize: 11,
  whiteSpace: 'nowrap',
  height: 22,
  lineHeight: '20px',
  verticalAlign: 'middle',
};

const SLOT_W = 14;            // min 10-min column width (kikxxl-style dense grid)

const SLOT_TD = {
  minWidth: SLOT_W,
  height: 22,
  padding: 0,
  border: '1px solid #ebebeb',
  verticalAlign: 'middle',
};

const WEEK_SUMMARY_STYLE = {
  background: '#e8edf2',
  borderTop: '2px solid #90a4ae',
  borderBottom: '2px solid #90a4ae',
};

// ── Utilities ─────────────────────────────────────────────────────────────────

function generateSlots(startTime, endTime) {
  const parse = t => { const [h, mm] = (t || '').split(':').map(Number); return h * 60 + (mm || 0); };
  const start = parse(startTime);
  const end   = parse(endTime);
  const slots = [];
  const hhmm = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  for (let t = start; t < end; t += INTERVAL_MIN) {
    slots.push({
      minutes:  t,
      label:    hhmm(t),
      // Full interval label as shown in the grid header, e.g. "07:00-07:15"
      rangeLabel: `${hhmm(t)}-${hhmm(t + INTERVAL_MIN)}`,
      isHour:   t % 60 === 0,
    });
  }
  return slots;
}

function getISOWeek(dateStr) {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const w1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - w1.getTime()) / 86400000 - 3 + (w1.getDay() + 6) % 7) / 7);
}

function groupByWeek(rows) {
  const groups = [];
  rows.forEach(row => {
    const d      = new Date(row.date);
    const offset = d.getDay() === 0 ? 6 : d.getDay() - 1;
    const mon    = new Date(d);
    mon.setDate(d.getDate() - offset);
    const key  = mon.toISOString().slice(0, 10);
    const last = groups[groups.length - 1];
    if (!last || last.key !== key) groups.push({ key, weekNum: getISOWeek(row.date), rows: [] });
    groups[groups.length - 1].rows.push(row);
  });
  return groups;
}

/**
 * Returns local clock minutes (0–1439) from an ISO timestamp.
 * Handles timezone: uses the browser's local time.
 */
function localMin(isoStr) {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  return isNaN(d) ? null : d.getHours() * 60 + d.getMinutes();
}

function schedMin(timeStr) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + (m || 0);
}

function fmtHM(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  return `${Math.floor(m / 60)}:${('0' + (m % 60)).slice(-2)}`;
}

function fmtDiff(diff) {
  if (diff == null) return '';
  const abs = Math.abs(diff);
  return `${diff >= 0 ? '+' : '-'}${fmtHM(abs)}`;
}

function dayFmt(dateStr) {
  return dateStr ? `${dateStr.slice(8)}.${dateStr.slice(5, 7)}.` : '';
}

function timeRange(start, end) {
  if (!start || !end) return '';
  return `${String(start).slice(0, 5)}–${String(end).slice(0, 5)}`;
}

/**
 * Determines what is happening in a given 30-min slot for a given day row.
 * Returns: 'work' | 'overtime' | 'break' | 'absent' | 'dayoff' | 'outside'
 */
function slotType(slotMinutes, row) {
  if (!row.is_work_day) return 'dayoff';
  if (!row.check_in) {
    // No attendance on a planned work day → surface the PLANNED orar window so the
    // assigned schedule stays visible (instead of a flat red row). Slots inside the
    // scheduled hours become 'planned'; outside stays blank. Falls back to 'absent'
    // when the worker has no schedule (no start/end available).
    const ssMin = schedMin(row.schedule_start);
    const seMin = schedMin(row.schedule_end);
    if (ssMin !== null && seMin !== null) {
      return (slotMinutes >= ssMin && slotMinutes < seMin) ? 'planned' : 'outside';
    }
    return 'absent';
  }

  const ciMin = localMin(row.check_in);
  const coMin = row.check_out ? localMin(row.check_out) : 24 * 60;

  if (ciMin === null || slotMinutes < ciMin)    return 'outside';
  if (coMin !== null && slotMinutes >= coMin)   return 'outside';

  // Break window?
  for (const br of (row.breaks || [])) {
    if (br.start && br.end) {
      const bsMin = localMin(br.start);
      const beMin = localMin(br.end);
      if (bsMin !== null && beMin !== null && slotMinutes >= bsMin && slotMinutes < beMin)
        return 'break';
    }
  }

  // Overtime (past scheduled end)?
  const seMin = schedMin(row.schedule_end);
  if (seMin !== null && slotMinutes >= seMin) return 'overtime';

  return 'work';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SlotCell({ slotMinutes, row, isSchedStartCol, isSchedEndCol, isAdmin, isExcluded, onSlotClick }) {
  const type       = slotType(slotMinutes, row);
  const clickable  = isAdmin && (type === 'work' || type === 'overtime' || isExcluded);
  const effectiveType = isExcluded ? 'excluded' : type;
  const bg         = SLOT_BG[effectiveType] || 'transparent';
  return (
    <td
      title={isExcluded ? '✕ Hequr manualisht — kliko për të ndryshuar' : clickable ? 'Kliko për të korrigjuar' : undefined}
      onClick={clickable ? onSlotClick : undefined}
      style={{
        ...SLOT_TD,
        background:  bg,
        borderLeft:  isSchedStartCol ? '2px solid #1565c0' : SLOT_TD.border,
        borderRight: isSchedEndCol   ? '2px solid #7b1fa2' : SLOT_TD.border,
        cursor:      clickable ? 'pointer' : 'default',
        // Faint diagonal stripes on excluded slots so they stay visible when printed
        ...(isExcluded ? {
          backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 2px, rgba(255,255,255,0.25) 2px, rgba(255,255,255,0.25) 4px)',
        } : {}),
        // Planned-but-not-worked: bold solid-blue hatch marks the assigned orar
        ...(effectiveType === 'planned' ? {
          backgroundImage: 'repeating-linear-gradient(135deg, rgba(21,101,192,0.85), rgba(21,101,192,0.85) 3px, rgba(13,71,161,1) 3px, rgba(13,71,161,1) 6px)',
        } : {}),
      }}
    />
  );
}

function WeekSummaryRow({ weekGroup, slots, isAdmin }) {
  const wd         = weekGroup.rows.filter(r => r.is_work_day);
  const weekendRows = weekGroup.rows.filter(r => !r.is_work_day && r.check_in);
  const plan       = wd.reduce((s, r) => s + (Number(r.planned_minutes)    || 0), 0);
  const net        = wd.reduce((s, r) => s + (Number(r.net_worked_minutes) || 0), 0);
  const ot         = wd.reduce((s, r) => s + (Number(r.overtime_minutes)   || 0), 0);
  const weekendMin = weekendRows.reduce((s, r) => s + (Number(r.net_worked_minutes) || 0), 0);
  const absent     = wd.filter(r => r.compliance_status === 'absent').length;
  const regular    = Math.max(0, net - ot);

  const st  = { ...BASE_TD, fontSize: 10, fontWeight: 600, ...WEEK_SUMMARY_STYLE, textAlign: 'center', padding: '1px 5px' };
  const val = { fontWeight: 800, fontSize: 11 };

  const fixedExtra = isAdmin ? 1 : 0;

  return (
    <tr style={{ ...WEEK_SUMMARY_STYLE }}>
      {/* Week label */}
      <td style={{ ...st, color: '#1565c0', fontWeight: 800, width: 28, position: 'sticky', left: 0, zIndex: 1 }}>
        KW{weekGroup.weekNum}
      </td>
      {/* Stats spread across fixed columns */}
      <td style={{ ...st, whiteSpace: 'nowrap', position: 'sticky', left: 28, zIndex: 1 }}>
        <span style={{ color: '#1565c0' }}>Reg: </span>
        <span style={{ ...val, color: '#1565c0' }}>{fmtHM(regular)}</span>
      </td>
      <td style={{ ...st, whiteSpace: 'nowrap', position: 'sticky', left: 82, zIndex: 1 }}>
        <span style={{ color: '#7b1fa2' }}>OT: </span>
        <span style={{ ...val, color: ot > 0 ? '#7b1fa2' : '#aaa' }}>{fmtHM(ot)}</span>
      </td>
      <td style={{ ...st, whiteSpace: 'nowrap', position: 'sticky', left: 112, zIndex: 1 }}>
        <span style={{ color: '#2e7d32' }}>Plan: </span>
        <span style={{ ...val, color: '#2e7d32' }}>{fmtHM(plan)}</span>
      </td>
      {/* Projekti column placeholder (per-day detail; no weekly aggregate) */}
      <td style={{ ...st, position: 'sticky', left: 182, zIndex: 1 }} />
      <td style={{ ...st, whiteSpace: 'nowrap', position: 'sticky', left: 278, zIndex: 1 }}>
        <span style={{ color: '#b36200' }}>WE: </span>
        <span style={{ ...val, color: weekendMin > 0 ? '#b36200' : '#aaa' }}>{fmtHM(weekendMin)}</span>
      </td>
      {/* Weekly total hours (worked, or planned when nothing worked yet) */}
      <td style={{ ...st, whiteSpace: 'nowrap', position: 'sticky', left: 304, zIndex: 1, borderRight: '2px solid #90a4ae' }}>
        <span style={{ ...val, color: '#263238' }}>{fmtHM(net > 0 ? net : plan)}</span>
      </td>
      {fixedExtra > 0 && <td style={{ ...st, position: 'sticky', left: 346, zIndex: 1, borderRight: '2px solid #90a4ae' }} />}
      {/* Slot cells */}
      {slots.map(slot => (
        <td key={slot.minutes} style={{ ...SLOT_TD, ...WEEK_SUMMARY_STYLE, background: '#e8edf2' }} />
      ))}
    </tr>
  );
}

// ── Main Export ───────────────────────────────────────────────────────────────

/**
 * slotEdits — plain object built by the parent (MonthlyIndividualWorkScheduleReport):
 *   key = `${adj_date}:${slot_minute}`, e.g. "2026-03-15:870"
 *   value = { excluded: true, reason, adjusted_by_name, adjusted_at }
 * onSlotEdit(row, slotMinutes) — fires when an admin clicks an editable cell.
 */
export default function WorkScheduleMatrix({ rows, scheduleStart, scheduleEnd, isAdmin, readOnly = false, onEditDay, slotEdits, onSlotEdit }) {
  const { t } = useTranslation();
  // Expand the visible range to cover schedule ± buffer AND all actual check-in/check-out times
  const effStart = useMemo(() => {
    let minH = scheduleStart ? Math.max(5, parseInt(scheduleStart.split(':')[0], 10) - 1) : 6;
    rows.forEach(r => {
      if (r.check_in) {
        const d = new Date(r.check_in);
        if (!isNaN(d)) minH = Math.min(minH, Math.max(5, d.getHours() - 0));
      }
    });
    return `${String(minH).padStart(2, '0')}:00`;
  }, [scheduleStart, rows]);

  const effEnd = useMemo(() => {
    let maxH = scheduleEnd ? Math.min(23, parseInt(scheduleEnd.split(':')[0], 10) + 2) : 20;
    rows.forEach(r => {
      if (r.check_out) {
        const d = new Date(r.check_out);
        if (!isNaN(d)) maxH = Math.max(maxH, Math.min(23, d.getHours() + 1));
      }
      // also cover breaks that may end late
      (r.breaks || []).forEach(br => {
        if (br.end) {
          const d = new Date(br.end);
          if (!isNaN(d)) maxH = Math.max(maxH, Math.min(23, d.getHours() + 1));
        }
      });
    });
    return `${String(maxH).padStart(2, '0')}:00`;
  }, [scheduleEnd, rows]);

  const slots   = useMemo(() => generateSlots(effStart, effEnd), [effStart, effEnd]);
  const weeks   = useMemo(() => groupByWeek(rows), [rows]);

  const ssMin = useMemo(() => schedMin(scheduleStart), [scheduleStart]);
  const seMin = useMemo(() => schedMin(scheduleEnd),   [scheduleEnd]);

  // Sticky-left offsets for the frozen leading columns (kikxxl-style frozen pane)
  const L = { kw: 0, data: 28, dita: 82, orari: 112, proj: 182, np: 278, tot: 304, edit: 346 };

  // The last fixed column ("Korrigjim") is always present: admins edit each day,
  // workers open it read-only (ⓘ) to see the day's details and any correction
  // that was intervened — but cannot edit.
  // `readOnly` fiton mbi `isAdmin`: një ekran vetëm-shikim nuk guxon të ketë
  // kontrolle që premtojnë ndryshim, edhe kur ai që shikon ka të drejtë ta bëjë
  // atë ndryshim diku tjetër.
  const canEdit     = !!isAdmin && !readOnly;
  const showEditCol = !readOnly;

  return (
    <Box
      sx={{
        overflowX: 'auto',
        fontFamily: '"Roboto Mono", "Courier New", monospace',
        fontSize: 11,
        // Print: landscape, shrink to fit
        '@media print': {
          overflow: 'visible',
          fontSize: 8,
          '& td, & th': { fontSize: '8px !important' },
        },
      }}
    >
      <table
        style={{
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          // Responsive: fill the available width; the time-slot columns (which carry
          // no explicit width) share the leftover space equally and stretch to fit.
          // minWidth keeps the labels readable on narrow screens (scroll kicks in).
          width: '100%',
          minWidth: `${(showEditCol ? 274 : 250) + 96 + slots.length * 13}px`,
          borderTop: '2px solid #1565c0',
          borderBottom: '2px solid #1565c0',
        }}
      >
        <thead>
          {/* Single header row — fixed column titles + per-slot 10-min interval labels */}
          <tr>
            <th style={{ ...BASE_TH, width: 28, color: '#1565c0', position: 'sticky', left: L.kw,   zIndex: 3, verticalAlign: 'bottom', height: 78 }}>KW</th>
            <th style={{ ...BASE_TH, width: 54, position: 'sticky', left: L.data,  zIndex: 3, verticalAlign: 'bottom' }}>Data</th>
            <th style={{ ...BASE_TH, width: 30, position: 'sticky', left: L.dita,  zIndex: 3, verticalAlign: 'bottom' }}>Dita</th>
            <th style={{ ...BASE_TH, width: 70, position: 'sticky', left: L.orari, zIndex: 3, verticalAlign: 'bottom' }}>Orari</th>
            <th style={{ ...BASE_TH, width: 96, color: '#1565c0', position: 'sticky', left: L.proj, zIndex: 3, verticalAlign: 'bottom' }}>{t('report.project')}</th>
            <th style={{ ...BASE_TH, width: 26, color: '#c62828', position: 'sticky', left: L.np, zIndex: 3, verticalAlign: 'bottom' }}>NP</th>
            <th style={{ ...BASE_TH, width: 42, position: 'sticky', left: L.tot, zIndex: 3, verticalAlign: 'bottom', borderRight: '2px solid #90a4ae' }}>Totali</th>
            {showEditCol && <th style={{ ...BASE_TH, width: 24, position: 'sticky', left: L.edit, zIndex: 3, borderRight: '2px solid #90a4ae' }} />}

            {slots.map(slot => {
              const beforeSched = ssMin !== null && slot.minutes < ssMin;
              const afterSched  = seMin !== null && slot.minutes >= seMin;
              const outside     = beforeSched || afterSched;
              return (
                <th
                  key={slot.minutes}
                  style={{
                    ...BASE_TH,
                    minWidth: SLOT_W,
                    padding: 0,
                    fontSize: 8,
                    fontWeight: 600,
                    height: 78,
                    verticalAlign: 'middle',
                    background: outside ? '#e6e6e6' : (slot.isHour ? '#ececec' : '#f4f4f4'),
                    color: ssMin === slot.minutes ? '#1565c0' : seMin === slot.minutes ? '#7b1fa2' : '#666',
                    borderLeft: slot.isHour ? '2px solid #bbb' : '1px solid #e0e0e0',
                  }}
                >
                  <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap', display: 'inline-block', lineHeight: 1, letterSpacing: 0.5 }}>
                    {slot.rangeLabel}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {weeks.map(week => (
            <React.Fragment key={week.key}>
              {week.rows.map((row, ri) => {
                const rowBg =
                  !row.is_work_day ? ROW_BG.dayoff :
                  row.compliance_status === 'absent' ? ROW_BG.absent :
                  ROW_BG.normal;

                const npCode =
                  !row.is_work_day ? '—' :
                  row.compliance_status === 'absent' ? 'A' :
                  '';

                const npColor =
                  npCode === 'A' ? '#c62828' :
                  npCode === '—' ? '#bbb' :
                  'transparent';

                const isFirstOfWeek = ri === 0;
                // Solid bg for sticky cells (transparent won't work with sticky)
                const stickyBg = !row.is_work_day ? '#f9f9f9' : row.compliance_status === 'absent' ? '#fff7f7' : '#ffffff';

                return (
                  <tr
                    key={row.date}
                    style={{
                      background: rowBg,
                      borderTop: isFirstOfWeek ? '2px solid #90a4ae' : undefined,
                    }}
                  >
                    {/* WEEK column — show number only on first row of week */}
                    <td style={{ ...BASE_TD, textAlign: 'center', fontWeight: 800, fontSize: 10, color: '#1565c0', width: 28, position: 'sticky', left: 0, zIndex: 1, background: stickyBg }}>
                      {isFirstOfWeek ? week.weekNum : ''}
                    </td>

                    {/* Date */}
                    <td style={{ ...BASE_TD, textAlign: 'center', position: 'sticky', left: 28, zIndex: 1, background: stickyBg }}>
                      <span style={{ color: !row.is_work_day ? '#aaa' : '#222' }}>
                        {dayFmt(row.date)}
                      </span>
                    </td>

                    {/* Day short */}
                    <td style={{ ...BASE_TD, textAlign: 'center', color: !row.is_work_day ? '#bbb' : '#444', width: 30, position: 'sticky', left: 82, zIndex: 1, background: stickyBg }}>
                      {row.day_short}
                    </td>

                    {/* Planned shift */}
                    <td style={{ ...BASE_TD, textAlign: 'center', fontSize: 10, color: '#555', position: 'sticky', left: 112, zIndex: 1, background: stickyBg }}>
                      {row.is_work_day ? timeRange(row.schedule_start, row.schedule_end) : ''}
                      {/* Lloji i punës i ditës (working_types): pikë me ngjyrën e tipit, emri,
                          dhe «PA PAGESË» kur nuk paguhet — lexohet pa hapur asgjë. */}
                      {row.is_work_day && row.working_type_code && row.working_type_code !== 'PRODUCTIVE' && (
                        <div title={`${row.working_type_name || row.working_type_code} · ${row.working_type_paid === false ? 'nuk paguhet' : 'paguhet'} (${row.working_type_source})`}
                          data-testid="day-working-type"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, fontSize: 9, lineHeight: '11px', fontWeight: 700,
                            color: row.working_type_paid === false ? '#b91c1c' : '#374151', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                          <span style={{ width: 7, height: 7, borderRadius: 2, background: row.working_type_color || '#64748B', flexShrink: 0 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 52 }}>{row.working_type_name || row.working_type_code}</span>
                          {row.working_type_accrue ? <span>·AKUMULOHET</span> : row.working_type_paid === false && <span>·PA PAGESË</span>}
                        </div>
                      )}
                    </td>

                    {/* Project(s) planned for this day, with the planned time block
                        (a day can have 2–3 projects worked back-to-back). */}
                    <td style={{ ...BASE_TD, padding: '1px 4px', textAlign: 'left', position: 'sticky', left: L.proj, zIndex: 1, background: stickyBg, width: 96, maxWidth: 96, overflow: 'hidden' }}>
                      {(row.projects || []).map(p => (
                        <div key={p.id} style={{ marginBottom: 1 }}>
                          <div style={{ fontSize: 8.5, fontWeight: 700, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: row.is_work_day ? '#1a2b47' : '#aaa' }}>
                            {p.name}
                          </div>
                          <div style={{ fontSize: 8.5, lineHeight: 1.15, color: '#1565c0', whiteSpace: 'nowrap' }}>
                            {p.start ? `${p.start}–${p.end}` : (p.hours ? `${p.hours}h` : '')}
                          </div>
                        </div>
                      ))}
                    </td>

                    {/* NP (absence code) */}
                    <td style={{ ...BASE_TD, textAlign: 'center', fontWeight: 700, color: npColor, position: 'sticky', left: L.np, zIndex: 1, background: stickyBg }}>
                      {npCode}
                    </td>

                    {/* Totali i orëve — worked hours, or planned when not yet worked */}
                    {(() => {
                      const net    = Number(row.net_worked_minutes) || 0;
                      const dayMin = net > 0 ? net : (row.is_work_day ? (Number(row.planned_minutes) || 0) : 0);
                      return (
                        <td style={{ ...BASE_TD, textAlign: 'center', fontWeight: 700, fontSize: 10, color: dayMin > 0 ? '#222' : '#bbb', position: 'sticky', left: L.tot, zIndex: 1, background: stickyBg, borderRight: '2px solid #c0cdd5' }}>
                          {dayMin > 0 ? fmtHM(dayMin) : (row.is_work_day ? '0:00' : '')}
                        </td>
                      );
                    })()}

                    {/* Korrigjim — edit (admin) or read-only view (worker, only on adjusted days) */}
                    {showEditCol && (
                      <td style={{ ...BASE_TD, textAlign: 'center', padding: '0 2px', position: 'sticky', left: L.edit, zIndex: 1, background: stickyBg, borderRight: '2px solid #c0cdd5' }}>
                        {canEdit && row.is_work_day ? (
                          <Tooltip title={row.is_adjusted ? `${t('report.correctedBy')}: ${row.adj_reason || ''}` : t('report.correct')}>
                            <span
                              onClick={() => onEditDay && onEditDay(row)}
                              style={{
                                cursor: 'pointer',
                                fontSize: 13,
                                color: row.is_adjusted ? '#7b1fa2' : '#1565c0',
                                userSelect: 'none',
                              }}
                              role="button"
                              aria-label={t('report.correctDay')}
                            >
                              {row.is_adjusted ? '✎̲' : '✎'}
                            </span>
                          </Tooltip>
                        ) : (!canEdit && row.is_work_day ? (
                          <Tooltip title={row.is_adjusted ? `${t('report.correctedBy')}: ${row.adj_reason || ''}` : t('report.viewDay')}>
                            <Box
                              component="span"
                              onClick={() => onEditDay && onEditDay(row)}
                              role="button"
                              aria-label={t('report.viewDay')}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: row.is_adjusted ? '#7b1fa2' : '#b0bec5',
                                transition: 'color .1s',
                                '&:hover': { color: row.is_adjusted ? '#6a1b9a' : '#1565c0' },
                              }}
                            >
                              {row.is_adjusted
                                ? <EditNoteIcon sx={{ fontSize: 16 }} />
                                : <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
                            </Box>
                          </Tooltip>
                        ) : null)}
                      </td>
                    )}

                    {/* Time-slot cells */}
                    {slots.map(slot => {
                      const editKey   = `${row.date}:${slot.minutes}`;
                      const slotEdit  = slotEdits ? slotEdits[editKey] : null;
                      const isExcluded = !!(slotEdit && slotEdit.excluded);
                      return (
                        <SlotCell
                          key={slot.minutes}
                          slotMinutes={slot.minutes}
                          row={row}
                          isSchedStartCol={ssMin === slot.minutes}
                          isSchedEndCol={seMin === slot.minutes}
                          isAdmin={canEdit}
                          isExcluded={isExcluded}
                          onSlotClick={() => onSlotEdit && onSlotEdit(row, slot.minutes, slotEdit)}
                        />
                      );
                    })}
                  </tr>
                );
              })}

              {/* Weekly summary row */}
              <WeekSummaryRow weekGroup={week} slots={slots} isAdmin={showEditCol} />
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </Box>
  );
}
